import Swiper, { Navigation } from 'swiper';

const registeredWrappers = new WeakSet();
const wrappersRegistry = new Set();
let resizeFrame = null;
let resizeListenerAdded = false;

const syncSolutionsCardMinHeights = (wrapper) => {
    wrapper.querySelectorAll('.solutions-card').forEach((card) => {
        const content = card.querySelector('.solutions-card__content');

        if (!content) {
            return;
        }

        card.style.removeProperty('--solutions-card-min-height');

        const styles = window.getComputedStyle(card);
        const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        const minHeight = Math.ceil(content.scrollHeight + verticalPadding);

        card.style.setProperty('--solutions-card-min-height', `${minHeight}px`);
    });

    const swiper = wrapper.querySelector('.swiper')?.swiper;

    if (swiper && !swiper.destroyed) {
        swiper.update();
    }
};

const scheduleSolutionsCardMinHeightsSync = () => {
    if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;

        wrappersRegistry.forEach((wrapper) => {
            syncSolutionsCardMinHeights(wrapper);
        });
    });
};

const initSolutionsSwiper = (scope = document) => {
    const wrappers = scope.querySelectorAll('.solutions-swiper-wr');

    wrappers.forEach((wrapper) => {
        const swiperEl = wrapper.querySelector('.swiper');

        if (!swiperEl) {
            return;
        }

        if (!registeredWrappers.has(wrapper)) {
            registeredWrappers.add(wrapper);
            wrappersRegistry.add(wrapper);
        }

        const slidesCount = swiperEl.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length;

        if (slidesCount < 3) {
            swiperEl.classList.add('no-swiper-type');
            syncSolutionsCardMinHeights(wrapper);
            return;
        }

        swiperEl.classList.remove('no-swiper-type');

        if (swiperEl.swiper && !swiperEl.swiper.destroyed) {
            syncSolutionsCardMinHeights(wrapper);
            return;
        }

        const nextEl = wrapper.querySelector('.swiper-button-next');
        const prevEl = wrapper.querySelector('.swiper-button-prev');

        new Swiper(swiperEl, {
            modules: [Navigation],
            observer: true,
            observeParents: true,
            slidesPerView: 1,
            spaceBetween: 20,
            threshold: 10,
            navigation: nextEl && prevEl ? {
                nextEl,
                prevEl,
            } : undefined,
            breakpoints: {
                768: {
                    slidesPerView: 2,
                    spaceBetween: 24,
                },
            },
        });

        syncSolutionsCardMinHeights(wrapper);
    });

    if (wrappers.length && !resizeListenerAdded) {
        resizeListenerAdded = true;
        window.addEventListener('resize', scheduleSolutionsCardMinHeightsSync, { passive: true });
        window.addEventListener('load', scheduleSolutionsCardMinHeightsSync, { once: true });

        if (document.fonts?.ready) {
            document.fonts.ready.then(scheduleSolutionsCardMinHeightsSync);
        }
    }
};

const initializedSolutionCards = new WeakSet();

const initSolutionsCardMotion = (scope = document) => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        return;
    }

    scope.querySelectorAll('.solutions-card').forEach((card) => {
        if (initializedSolutionCards.has(card)) {
            return;
        }

        const bg = card.querySelector('.solutions-card__bg');

        if (!bg) {
            return;
        }

        initializedSolutionCards.add(card);

        let frameId = null;
        let pointerX = 0;
        let pointerY = 0;

        const updateBackgroundPosition = () => {
            const bounds = card.getBoundingClientRect();

            if (!bounds.width || !bounds.height) {
                frameId = null;
                return;
            }

            const offsetX = ((pointerX - bounds.left) / bounds.width - 0.5) * 20;
            const offsetY = ((pointerY - bounds.top) / bounds.height - 0.5) * 20;

            bg.style.transform = `scale(1.08) translate3d(${offsetX}px, ${offsetY}px, 0)`;
            frameId = null;
        };

        const resetBackgroundPosition = () => {
            card.classList.remove('is-motion-active');

            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
                frameId = null;
            }

            bg.style.removeProperty('transform');
        };

        card.addEventListener('pointerenter', () => {
            card.classList.add('is-motion-active');
        });

        card.addEventListener('pointermove', (event) => {
            pointerX = event.clientX;
            pointerY = event.clientY;

            if (frameId === null) {
                frameId = window.requestAnimationFrame(updateBackgroundPosition);
            }
        }, { passive: true });

        card.addEventListener('pointerleave', resetBackgroundPosition);
    });
};

initSolutionsSwiper();
initSolutionsCardMotion();
